/**
 * PRD.330 Phase 2 — Read-only report table.
 *
 * Renders the rows it is given, in the order given — it never filters,
 * sorts, slices or aggregates them (all of that is the query layer's job).
 * Header cells expose SEPARATE sort and filter buttons (the <th> itself is
 * not clickable); state is shown by icon shape + accessible names, not
 * colour alone. The header row is sticky inside the table's own scroll
 * container, which also scrolls horizontally for wide reports.
 */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Button } from '@helios/ui';
import type { ReportFacetOption } from '@helios/domain';
import { FunnelIcon, SortIcon } from './ReportIcons';
import { ReportFilterPopover } from './ReportFilterPopover';
import type { ReportController } from './useReportController';
import { buildFacetList, columnHeaderState, type ReportViewStatus } from './report-view-logic';
import { shouldReseedFacetDraft } from './report-state-logic';

export type ReportColumnRenderers<Row, C extends string> = Record<C, (row: Row) => ReactNode>;

export interface ReportTableMessages {
  loading: ReactNode;
  empty: ReactNode;
  noResults: ReactNode;
  error: ReactNode;
}

export interface ReportTableProps<Row, C extends string> {
  controller: ReportController<C>;
  rows: ReadonlyArray<Row>;
  rowKey: (row: Row) => string;
  render: ReportColumnRenderers<Row, C>;
  status: ReportViewStatus;
  /** True while a new query runs and previous rows are still shown. */
  refetching: boolean;
  /** Facet options supplied by the query layer (never derived from `rows`). */
  facets: Partial<Record<C, ReadonlyArray<ReportFacetOption>>>;
  /** Facet columns whose options are still loading — never shown as "no options". */
  facetLoading: Partial<Record<C, boolean>>;
  facetLabel: (columnId: C, value: string) => string;
  messages: ReportTableMessages;
  onRetry?: () => void;
}

const headerButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 32,
  padding: '0 4px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
  textTransform: 'inherit',
  letterSpacing: 'inherit',
  borderRadius: 'var(--radius-sm, 4px)',
};

export function ReportTable<Row, C extends string>(props: ReportTableProps<Row, C>) {
  const { controller, rows, status } = props;
  const { definition, query, state } = controller;
  // Filter trigger elements, used to anchor the popover (set during commit,
  // so they exist before any click can open a popover).
  const filterButtons = useRef(new Map<C, HTMLButtonElement>());

  const tableMinWidth = definition.columns.reduce((sum, c) => sum + (c.minWidth ?? 140), 0);
  const colCount = definition.columns.length;
  const openColumn = state.openColumn;
  const openColumnDef = openColumn
    ? definition.columns.find((c) => c.id === openColumn)
    : undefined;
  const openAnchor = openColumn ? filterButtons.current.get(openColumn) : undefined;
  const facetValues = (columnId: C) => (props.facets[columnId] ?? []).map((o) => o.value);
  const openFacetLoading = openColumn ? props.facetLoading[openColumn] === true : false;

  // A facet popover opened WHILE its options were loading was seeded with
  // nothing checked. When they arrive, re-seed it once — only if the user
  // hasn't edited it. (A user who clears all after options loaded is never
  // re-seeded: `openedWhileLoading` is false for them.)
  const openedWhileLoading = useRef(false);
  const { openFilter } = controller;
  const reseedValues = openColumn ? props.facets[openColumn] : undefined;
  const reseed = openedWhileLoading.current && !openFacetLoading && shouldReseedFacetDraft(state);
  useEffect(() => {
    if (!openColumn) openedWhileLoading.current = false;
    if (!reseed || !openColumn) return;
    openedWhileLoading.current = false;
    if (reseedValues && reseedValues.length > 0) {
      openFilter(
        openColumn,
        reseedValues.map((o) => o.value),
      );
    }
  }, [reseed, openColumn, reseedValues, openFilter]);

  const message = (content: ReactNode, action?: ReactNode) => (
    <tr>
      <td
        colSpan={colCount}
        style={{
          padding: 'var(--sp-12) var(--sp-6)',
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        <div>{content}</div>
        {action && <div style={{ marginTop: 'var(--sp-3)' }}>{action}</div>}
      </td>
    </tr>
  );

  let body: ReactNode;
  if (status === 'error') {
    body = message(
      props.messages.error,
      props.onRetry && (
        <Button type="button" size="sm" variant="outline" onClick={props.onRetry}>
          Retry
        </Button>
      ),
    );
  } else if (status === 'loading' && rows.length === 0) {
    body = message(props.messages.loading);
  } else if (status === 'empty') {
    body = message(props.messages.empty);
  } else if (status === 'no-results') {
    body = message(
      props.messages.noResults,
      <Button type="button" size="sm" variant="outline" onClick={controller.clearFilters}>
        Clear filters
      </Button>,
    );
  } else {
    body = rows.map((row) => (
      <tr key={props.rowKey(row)} style={{ borderBottom: '1px solid var(--border)' }}>
        {definition.columns.map((column) => (
          <td
            key={column.id}
            style={{
              padding: 'var(--sp-3) var(--sp-4)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              textAlign: column.align ?? 'left',
              verticalAlign: 'top',
              fontVariantNumeric: column.valueType === 'money' ? 'tabular-nums' : undefined,
            }}
          >
            {props.render[column.id](row)}
          </td>
        ))}
      </tr>
    ));
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 200,
        overflow: 'auto',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <table
        style={{
          width: '100%',
          minWidth: tableMinWidth,
          borderCollapse: 'separate',
          borderSpacing: 0,
          opacity: props.refetching ? 0.55 : 1,
          transition: 'opacity 120ms ease',
        }}
      >
        <thead>
          <tr>
            {definition.columns.map((column) => {
              const h = columnHeaderState(definition, query, column.id);
              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={h.sortable ? h.ariaSort : undefined}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    minWidth: column.minWidth,
                    padding: 'var(--sp-2) var(--sp-3)',
                    background: 'var(--bg-surface-2, #f8fafc)',
                    borderBottom: '1px solid var(--border)',
                    textAlign: column.align ?? 'left',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {h.sortable ? (
                      <button
                        type="button"
                        aria-label={h.sortButtonLabel}
                        title={h.sortButtonLabel}
                        onClick={() => controller.toggleSort(column.id)}
                        style={{
                          ...headerButtonStyle,
                          color: h.sortDirection && !h.sortIsDefault ? 'var(--text)' : 'inherit',
                        }}
                      >
                        {column.label}
                        <span
                          style={{ opacity: h.sortDirection ? 1 : 0.4, display: 'inline-flex' }}
                        >
                          <SortIcon direction={h.sortDirection} />
                        </span>
                      </button>
                    ) : (
                      <span style={{ padding: '0 4px' }}>{column.label}</span>
                    )}
                    {h.filterable && (
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) filterButtons.current.set(column.id, el);
                          else filterButtons.current.delete(column.id);
                        }}
                        aria-label={h.filterButtonLabel}
                        title={h.filterButtonLabel}
                        aria-haspopup="dialog"
                        aria-expanded={openColumn === column.id}
                        onClick={() => {
                          if (openColumn === column.id) {
                            controller.cancelDraft();
                          } else {
                            openedWhileLoading.current = props.facetLoading[column.id] === true;
                            controller.openFilter(column.id, facetValues(column.id));
                          }
                        }}
                        style={{
                          ...headerButtonStyle,
                          width: 32,
                          justifyContent: 'center',
                          color: h.filterActive ? 'var(--color-primary)' : 'inherit',
                          background: h.filterActive
                            ? 'var(--color-primary-subtle, rgba(37, 99, 235, 0.08))'
                            : 'transparent',
                        }}
                      >
                        <FunnelIcon active={h.filterActive} />
                      </button>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>

      {openColumn && openColumnDef && openAnchor && state.draft && (
        <ReportFilterPopover
          anchor={openAnchor}
          columnLabel={openColumnDef.label}
          draft={state.draft}
          error={state.draftError}
          facetItems={
            state.draft.kind === 'values'
              ? buildFacetList(props.facets[openColumn] ?? [], state.draft.selected, (v) =>
                  props.facetLabel(openColumn, v),
                )
              : []
          }
          optionsLoading={openFacetLoading}
          sortable={openColumnDef.sortable}
          onEditDraft={controller.editDraft}
          onSelectVisible={controller.selectVisible}
          onClearVisible={controller.clearVisible}
          onApply={() => controller.applyDraft(facetValues(openColumn))}
          onCancel={controller.cancelDraft}
          onClear={() => controller.clearColumn(openColumn)}
          onSort={(direction) => controller.setSort(openColumn, direction)}
        />
      )}
    </div>
  );
}
